import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

interface UseTrackActivityProps {
  /**
   * The time in milliseconds after which the mouse is considered to have stopped moving.
   */
  delay?: number;

  /**
   * A React ref to the container element to attach the mouse move event listener to.
   * If not provided, the document object will be used.
   */
  containerRef?: React.RefObject<any>;
}

interface UseTrackActivityReturn {
  isMouseMoving: boolean;
  isTouchDevice: boolean;
  isMouseClicking: boolean;
}

/**
 * This hook tracks mouse movement and click activity within a specified container element.
 * It is particularly useful for UI elements that need to appear or disappear
 * based on mouse activity, such as video player controls that show when the mouse
 * moves or clicks and hide when it's inactive.
 */

export const MOUSE_MOVE_DELAY = 300;
export const MOUSE_CLICK_DELAY = 300;

export default function useTrackActivity({
  delay = MOUSE_MOVE_DELAY,
  containerRef,
}: UseTrackActivityProps): UseTrackActivityReturn {
  const [isMouseMoving, setIsMouseMoving] = useState(false);
  const [isMouseClicking, setIsMouseClicking] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const mouseMovingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const mouseClickingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const handleMouseMove = useCallback(() => {
    setIsMouseMoving(true);

    if (mouseMovingTimeoutRef.current) {
      clearTimeout(mouseMovingTimeoutRef.current);
    }

    mouseMovingTimeoutRef.current = setTimeout(() => {
      setIsMouseMoving(false);
    }, delay);
  }, [delay]);

  const handleMouseClick = useCallback(() => {
    setIsMouseClicking(true);

    if (mouseClickingTimeoutRef.current) {
      clearTimeout(mouseClickingTimeoutRef.current);
    }

    mouseClickingTimeoutRef.current = setTimeout(() => {
      setIsMouseClicking(false);
    }, MOUSE_CLICK_DELAY);
  }, []);

  useEffect(() => {
    setIsTouchDevice(Platform.OS !== 'web' || 'ontouchstart' in window);

    if (Platform.OS !== 'web') {
      return;
    }

    const target = containerRef?.current || document;

    target.addEventListener('mousemove', handleMouseMove);
    target.addEventListener('click', handleMouseClick);

    return () => {
      target.removeEventListener('mousemove', handleMouseMove);
      target.removeEventListener('click', handleMouseClick);

      if (mouseMovingTimeoutRef.current) {
        clearTimeout(mouseMovingTimeoutRef.current);
      }

      if (mouseClickingTimeoutRef.current) {
        clearTimeout(mouseClickingTimeoutRef.current);
      }
    };
  }, [handleMouseMove, handleMouseClick, containerRef]);

  return { isMouseMoving, isMouseClicking, isTouchDevice };
}
